import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import platformAPI from "../../../services/platformApi";
import { buildClinicPortalUrl, formatClinicHost } from "../../../utils/portalHost";
import s from "./platformAdmin.module.css";

const STATUS_LABEL = {
  trial: "Пробный",
  active: "Активен",
  suspended: "Отключён",
};

function formatRub(n) {
  return new Intl.NumberFormat("ru-RU").format(Number(n) || 0);
}

export default function PlatformTenantDetail() {
  const { id } = useParams();
  const [clinic, setClinic] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState({
    saas_plan_id: "",
    status: "",
    crm_monthly_price_rub: "",
    crm_billing_period: "monthly",
    crm_paid_until: "",
  });
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = () => {
    Promise.all([
      platformAPI.getClinic(id),
      platformAPI.getPlans(),
    ])
      .then(([detail, plansRes]) => {
        const c = detail.clinic;
        setClinic(c);
        setEntitlements(detail.entitlements);
        setPlans(plansRes.plans?.filter((p) => p.is_active) || []);
        setBilling({
          saas_plan_id: c.saas_plan_id || "",
          status: c.status,
          crm_monthly_price_rub: c.crm_monthly_price_rub ?? "",
          crm_billing_period: c.crm_billing_period || "monthly",
          crm_paid_until: c.crm_paid_until
            ? String(c.crm_paid_until).slice(0, 10)
            : "",
        });
      })
      .catch((e) =>
        setErr(e.response?.data?.error || "Клуб не найден"),
      );
  };

  useEffect(() => {
    load();
  }, [id]);

  const saveBilling = async (e) => {
    e.preventDefault();
    setErr("");
    setMsg("");
    try {
      await platformAPI.updateClinicBilling(id, {
        saas_plan_id: billing.saas_plan_id || null,
        status: billing.status,
        crm_monthly_price_rub:
          billing.crm_monthly_price_rub === ""
            ? null
            : Number(billing.crm_monthly_price_rub),
        crm_billing_period: billing.crm_billing_period,
        crm_paid_until: billing.crm_paid_until || null,
      });
      setMsg("Биллинг обновлён");
      load();
    } catch (e2) {
      setErr(e2.response?.data?.error || "Ошибка сохранения");
    }
  };

  if (!clinic && !err) {
    return <p className={s.subtitle}>Загрузка…</p>;
  }

  if (!clinic) {
    return (
      <>
        <div className={s.alertError}>{err}</div>
        <Link to="/admin/tenants" className={s.btn}>
          ← К списку
        </Link>
      </>
    );
  }

  const features = entitlements?.features || {};

  return (
    <>
      <div className={s.topbar}>
        <div>
          <h1 className={s.title}>{clinic.name}</h1>
          <p className={s.subtitle}>
            <a
              href={buildClinicPortalUrl(clinic.subdomain, "/manage")}
              target="_blank"
              rel="noopener noreferrer"
            >
              {formatClinicHost(clinic.subdomain)}
            </a>{" "}
            · {STATUS_LABEL[clinic.status] || clinic.status}
          </p>
        </div>
        <Link to="/admin/tenants" className={s.btn}>
          ← К списку
        </Link>
      </div>

      {msg ? <div className={s.alertSuccess}>{msg}</div> : null}
      {err ? <div className={s.alertError}>{err}</div> : null}

      <div className={s.grid}>
        <div className={s.card}>
          <div className={s.cardLabel}>Тренеры</div>
          <div className={s.cardValue}>{clinic.stats?.trainers ?? 0}</div>
        </div>
        <div className={s.card}>
          <div className={s.cardLabel}>Клиенты</div>
          <div className={s.cardValue}>{clinic.stats?.clients ?? 0}</div>
        </div>
        <div className={s.card}>
          <div className={s.cardLabel}>Абонементы</div>
          <div className={s.cardValue}>
            {clinic.stats?.activeMemberships ?? 0}
          </div>
        </div>
        <div className={s.card}>
          <div className={s.cardLabel}>Выручка 30 дней</div>
          <div className={s.cardValue}>
            {formatRub(clinic.stats?.revenue_30d)} ₽
          </div>
        </div>
      </div>

      <form className={s.panel} onSubmit={saveBilling}>
        <h2 className={s.panelTitle}>Тариф и биллинг</h2>
        <div className={s.featuresGrid}>
          <div className={s.field}>
            <label>SaaS-тариф</label>
            <select
              value={billing.saas_plan_id}
              onChange={(e) =>
                setBilling({ ...billing, saas_plan_id: e.target.value })
              }
            >
              <option value="">По legacy tier</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.monthly_price_rub} ₽/мес)
                </option>
              ))}
            </select>
          </div>
          <div className={s.field}>
            <label>Статус</label>
            <select
              value={billing.status}
              onChange={(e) =>
                setBilling({ ...billing, status: e.target.value })
              }
            >
              <option value="trial">Пробный</option>
              <option value="active">Активен</option>
              <option value="suspended">Отключён</option>
            </select>
          </div>
          <div className={s.field}>
            <label>Цена CRM / мес (₽)</label>
            <input
              type="number"
              min="0"
              value={billing.crm_monthly_price_rub}
              onChange={(e) =>
                setBilling({
                  ...billing,
                  crm_monthly_price_rub: e.target.value,
                })
              }
            />
          </div>
          <div className={s.field}>
            <label>Период</label>
            <select
              value={billing.crm_billing_period}
              onChange={(e) =>
                setBilling({
                  ...billing,
                  crm_billing_period: e.target.value,
                })
              }
            >
              <option value="monthly">Месяц</option>
              <option value="yearly">Год</option>
            </select>
          </div>
          <div className={s.field}>
            <label>Оплачено до</label>
            <input
              type="date"
              value={billing.crm_paid_until}
              onChange={(e) =>
                setBilling({ ...billing, crm_paid_until: e.target.value })
              }
            />
          </div>
        </div>
        <button type="submit" className={`${s.btn} ${s.btnPrimary}`}>
          Сохранить
        </button>
      </form>

      <div className={s.panel}>
        <h2 className={s.panelTitle}>
          Функции тарифа
          {entitlements?.plan?.name
            ? ` — ${entitlements.plan.name}`
            : ""}
        </h2>
        <div className={s.chips}>
          {Object.entries(features).map(([key, val]) => (
            <span
              key={key}
              className={`${s.chip} ${
                val === true || (typeof val === "number" && val > 0)
                  ? s.chipOn
                  : s.chipOff
              }`}
            >
              {key}: {String(val)}
            </span>
          ))}
        </div>
      </div>
    </>
  );
}
